import * as React from 'react'
import { useState, useEffect } from 'react'
import { format, startOfDay, endOfDay } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { type DateRange } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface DateRangePickerProps {
  value?: DateRange
  onChange: (range: DateRange | undefined) => void
  placeholder?: string
  className?: string
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Pick a date range',
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(value)

  // Sync internal state with external value when it changes
  useEffect(() => {
    setInternalRange(value)
  }, [value])

  const handleSelect = (range: DateRange | undefined) => {
    setInternalRange(range)
    // Only close and trigger onChange when both dates are selected and different
    if (range?.from && range?.to && range.from.getTime() !== range.to.getTime()) {
      setOpen(false)
      // Normalize: start of day for 'from', end of day for 'to'
      onChange({
        from: startOfDay(range.from),
        to: endOfDay(range.to),
      })
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'min-w-[200px] justify-start text-left font-medium',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value?.from ? (
            value.to ? (
              <>
                {format(value.from, 'LLL dd')} - {format(value.to, 'LLL dd, yyyy')}
              </>
            ) : (
              format(value.from, 'LLL dd, yyyy')
            )
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="range"
          defaultMonth={internalRange?.from}
          selected={internalRange}
          onSelect={handleSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  )
}
